import { IconButton, Tooltip } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';

import { Span, slug } from 'shared-components';

import { Status, Props, documentStatusIcons } from './types';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { FingerIcon } from '../../Finger/FingerIcon';
import { RiskIcon } from '../../RiskAction/RiskIcon';
import { FingerExpander } from '../../Finger/FingerExpander';
import { RiskAlert } from '../../RiskAction/RiskAlert';
import RenderDocInfo from './RenderDocInfo';

function Finger({
  id = 'finger',
  status,
  isDocumentFixed = false,
  isDeleting = false,
  reason,
  description,
  label,
  isCheckedOff = false,
  canCheckOff = false,
  onCheckOffRisk,
  onDeleteClick,
  children,
  userRole,
  deletedByTTL,
}: Props) {
  function checkOffRisk() {
    if (onCheckOffRisk) onCheckOffRisk();
  }

  const canViewApprove = hasPerm(userRole, P.ALL_DOCUMENTS_VIEW_APPROVAL);
  const canViewReject = hasPerm(userRole, P.ALL_DOCUMENTS_VIEW_REJECTION);
  const canPassScheduledInterview = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_PASS);
  const canResitScheduledInterview = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_RESIT);
  const canFailScheduledInterview = hasPerm(userRole, P.SCHEDULED_INTERVIEWS_FAIL);
  const canPassRecordedInterview = hasPerm(userRole, P.RECORDED_INTERVIEW_PASS);
  const canResitRecordedInterview = hasPerm(userRole, P.RECORDED_INTERVIEW_RESIT);
  const canFailRecordedInterview = hasPerm(userRole, P.RECORDED_INTERVIEW_FAIL);

  const deleteMode =
    isDeleting &&
    status !== Status.Rejected &&
    status !== Status.RejectedWithoutAlt &&
    status !== Status.Approved &&
    status !== Status.Replaced &&
    status !== Status.Skipped &&
    status !== Status.Failed &&
    status !== Status.Passed &&
    status !== Status.Cancelled;

  const getIconProps = () => {
    switch (status) {
      // Document outcomes
      case Status.Approved:
        if (canViewApprove) {
          return {
            ...documentStatusIcons.Approved,
          };
        }
        return {
          ...documentStatusIcons.Uploaded,
        };

      case Status.Rejected:
      case Status.RejectedWithoutAlt:
        if (canViewReject) {
          return {
            ...documentStatusIcons.Rejected,
          };
        }
        return {
          ...documentStatusIcons.Requested,
        };

      // Document statuses
      case Status.UploadedOK:
        return {
          ...documentStatusIcons.Uploaded,
        };
      default:
      case Status.Awaiting:
        return {
          ...documentStatusIcons.Requested,
        };
      case Status.UploadedError:
        if (isDocumentFixed) {
          return {
            ...documentStatusIcons.UploadFixed,
          };
        }
        return {
          ...documentStatusIcons.UploadFixRequested,
        };
      case Status.InError:
        return {
          ...documentStatusIcons.UploadError,
        };
      case Status.Replaced:
        return {
          ...documentStatusIcons.ReplacementRequested,
        };
      case Status.Skipped:
        return {
          ...documentStatusIcons.NotRequired,
        };

      // Info only documents
      case Status.InfoOnly:
      case Status.NotAcknowledged:
        return {
          ...documentStatusIcons.NotAcknowledged,
        };
      case Status.Acknowledged:
        return {
          ...documentStatusIcons.Acknowledged,
        };

      // Interview outcomes
      case Status.Passed:
        if (canPassScheduledInterview || canPassRecordedInterview) {
          return {
            ...documentStatusIcons.InterviewPassed,
          };
        }
        return {
          ...documentStatusIcons.InterviewNoActionRequired,
        };

      case Status.Resit:
        if (canResitScheduledInterview || canResitRecordedInterview) {
          return {
            ...documentStatusIcons.InterviewResit,
          };
        }
        return {
          ...documentStatusIcons.InterviewNoActionRequired,
        };
      case Status.Failed:
        if (canFailScheduledInterview || canFailRecordedInterview) {
          return {
            ...documentStatusIcons.InterviewFailed,
          };
        }
        return {
          ...documentStatusIcons.InterviewNoActionRequired,
        };

      // Booked interviews
      case Status.Requested:
        return {
          ...documentStatusIcons.BookedInterviewRequested,
        };

      case Status.Booked:
        return {
          ...documentStatusIcons.BookedInterviewBooked,
        };
      case Status.Cancelled:
        return {
          ...documentStatusIcons.BookedInterviewCancelled,
        };

      // Recorded interviews
      case Status.Invited:
        return {
          ...documentStatusIcons.RecordedInterviewInvited,
        };
      case Status.Recorded:
        return {
          ...documentStatusIcons.RecordedInterviewRecorded,
        };
      case Status.Attempted:
        return {
          ...documentStatusIcons.RecordedInterviewAttempted,
        };
    }
  };

  const Icon = () => {
    const label = 'Delete request';

    if (deleteMode) {
      return (
        <Tooltip hasArrow placement="top" label={label}>
          <IconButton
            aria-label={label}
            data-selector={`finger-icon-delete`}
            icon={<FontAwesomeIcon icon={faTrashAlt} />}
            onClick={onDeleteClick}
            colorScheme="red"
            isRound
          />
        </Tooltip>
      );
    }

    const { tooltip, color, icon } = getIconProps();

    return <FingerIcon data-selector={`finger-icon-${slug(status)}`} label={tooltip} color={color} icon={icon} />;
  };

  const Flag = () => {
    if (status === Status.UploadedError) {
      return (
        <Span data-selector={`${id}-risk-status-icon`}>
          <RiskIcon
            id={id}
            isDoc={true}
            isDocumentFixed={isDocumentFixed}
            isCheckedOff={isCheckedOff}
            canCheckOff={canCheckOff}
            onCheckOffRisk={checkOffRisk}
            boxProps={{ ml: 2 }}
          />
        </Span>
      );
    }
  };

  const Alert = () => {
    if (status === Status.UploadedError) {
      return (
        <RiskAlert
          id={id}
          isDoc={true}
          isDocumentFixed={isDocumentFixed}
          isCheckedOff={isCheckedOff}
          canCheckOff={canCheckOff}
          onCheckOffRisk={checkOffRisk}
        />
      );
    }
  };

  return (
    <FingerExpander id={id} label={label} icon={<Icon />} flag={<Flag />} alert={<Alert />}>
      <RenderDocInfo
        status={status}
        reason={reason}
        description={description}
        children={children}
        deletedByTTL={deletedByTTL}
      />
    </FingerExpander>
  );
}

export default Finger;

export { Status };
